import React, { Component } from 'react';
import { connect } from 'react-redux';

import { fetchAccountInfo } from 'actions/account/account';
import { redirectToAuthenticationFor } from 'actions/redirect';
import { extractEmailAuthToken, isEmailAuthTokenPresent, isMaybeAuthenticated } from 'helpers/AuthHelpers';
import {
  fetchSubscriptionsInfo,
  toggleSubscriptionBrand,
  toggleSubscriptionList,
  toggleSubscriptionStock,
  updateSubscriptions
} from 'actions/account/subscriptions';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import EmailListSubscriptionListItem from 'components/account/Subscriptions/EmailListSubscriptionListItem';
import BrandSubscriptionListItem from 'components/account/Subscriptions/BrandSubscriptionListItem';
import StockSubscriptionListItem from 'components/account/Subscriptions/StockSubscriptionListItem';
import AccountHeader from 'containers/account/AccountHeader';
import styles from 'styles/containers/account/subscriptions.scss';

export class Subscriptions extends Component {
  static fetchDataOnServer(store, location) {
    if (isMaybeAuthenticated(store, { checkForAuthToken: true })) {

      return (isEmailAuthTokenPresent(store)) ?
        store.dispatch(fetchSubscriptionsInfo()) :
        Promise.all([
          store.dispatch(fetchSubscriptionsInfo()),
          store.dispatch(fetchAccountInfo())
        ]);
    } else {
      return Promise.resolve(store.dispatch(redirectToAuthenticationFor(location)));
    }
  }

  constructor(props) {
    super(props);
    this.onUnsubscribeFromAll = this.onUnsubscribeFromAll.bind(this);
    this.onSaveChanges = this.onSaveChanges.bind(this);
    this.makeEmailListItems = this.makeEmailListItems.bind(this);
    this.makeBrandItems = this.makeBrandItems.bind(this);
    this.makeStockItems = this.makeStockItems.bind(this);
    this.makeSubscriptionsTable = this.makeSubscriptionsTable.bind(this);
    this.makeAccountHeader = this.makeAccountHeader.bind(this);
  }

  state = { emailLists: [], brandSubscriptions: [], stockSubscriptions: [] };

  componentDidMount() {
    const { subscriptionsInfo } = this.props;
    const { isLoaded } = subscriptionsInfo;
    // Fetch only if the subscriptions data is not loaded
    if (!isLoaded) {
      this.fetchData();
    }
  }

  onBrandChange = ({ target }) => {
    const { toggleSubscriptionBrand } = this.props;
    toggleSubscriptionBrand(target.value);
  };

  onListChange = ({ target }) => {
    const { toggleSubscriptionList } = this.props;
    toggleSubscriptionList(target.value);
  };

  onSaveChanges() {
    const { subscriptionsInfo: { subscriptionsDelta } } = this.props;
    this.props.updateSubscriptions(subscriptionsDelta);
  }

  onStockChange = ({ target }) => {
    const { toggleSubscriptionStock } = this.props;
    toggleSubscriptionStock(target.value);
  };

  onUnsubscribeFromAll() {
    const { subscriptionsInfo: { subscriptions }, updateSubscriptions } = this.props;
    const { emailLists, brandSubscriptions, stockSubscriptions } = subscriptions;

    const subscriptionsDelta = {
      emailLists: [],
      brandSubscriptions: [],
      stockSubscriptions: []
    };

    emailLists.forEach(list => {
      if (list.subscribed) {
        list.subscribed = false;
        subscriptionsDelta.emailLists.push(list);
      }
    });

    brandSubscriptions.forEach(brandSub => {
      brandSub.subscribed = false;
      subscriptionsDelta.brandSubscriptions.push(brandSub);
    });

    stockSubscriptions.forEach(stockSub => {
      stockSub.subscribed = false;
      subscriptionsDelta.stockSubscriptions.push(stockSub);
    });
    updateSubscriptions(subscriptionsDelta);
  }

  fetchData() {
    const { routing, fetchSubscriptionsInfo, fetchAccountInfo } = this.props;
    const emailAuthTokenPresent = !!extractEmailAuthToken(routing);
    fetchSubscriptionsInfo();
    if (!emailAuthTokenPresent) {
      fetchAccountInfo();
    }
  }

  makeAccountHeader() {
    return (
      <div className={styles.accountHeader}>
        <AccountHeader title="Email Subscription Preferences" showCustomerEmail={false} />
      </div>
    );
  }

  makeBrandItems(brandSubscriptions) {
    if (!brandSubscriptions.length) {
      return <tbody><tr><td>You have no brand subscriptions :(</td></tr></tbody>;
    } else {
      return <tbody>{brandSubscriptions.map(brand => {
        const { id, name, subscribed } = brand;
        return (
          <BrandSubscriptionListItem
            key={id}
            id={id}
            name={name}
            subscribed={subscribed}
            onBrandChange={this.onBrandChange}/>
        );
      })}</tbody>;
    }
  }

  makeEmailListItems(emailLists) {
    if (!emailLists.length) {
      return <tbody><tr><td>You have no email list subscriptions :(</td></tr></tbody>;
    } else {
      return <tbody>{emailLists.map(emailList => {
        const { emailListName, subscribed } = emailList;
        return (
          <EmailListSubscriptionListItem
            key={emailListName}
            onListChange={this.onListChange}
            subscribed={subscribed}
            emailListName={emailListName}/>
        );
      })}</tbody>;
    }
  }

  makeStockItems(stockSubscriptions) {
    if (!stockSubscriptions.length) {
      return <tbody><tr><td>You have no stock subscriptions :(</td></tr></tbody>;
    } else {
      return <tbody>{stockSubscriptions.map(product => {
        const { productName, asin, brand, color, size, width, imageId, subscribed } = product;
        return (
          <StockSubscriptionListItem
            key={asin}
            productName={productName}
            asin={asin}
            brand={brand}
            color={color}
            size={size}
            width={width}
            imageId={imageId}
            subscribed={subscribed}
            onStockChange={this.onStockChange}/>
        );
      })}</tbody>;
    }
  }

  makeSubscriptionsTable() {
    const { subscriptionsInfo: { subscriptions: { emailLists, brandSubscriptions, stockSubscriptions } } } = this.props;
    const { makeEmailListItems, makeBrandItems, makeStockItems } = this;

    return (
      <div className={styles.subscriptionsContainer}>
        {
          <div>
            <table className={styles.subscriptionsTable}>
              <thead>
                <tr>
                  <th>Marketing Messages</th>
                </tr>
              </thead>
              {makeEmailListItems(emailLists)}
            </table>
            <table className={styles.subscriptionsTable}>
              <thead>
                <tr>
                  <th>Brand, Initiative, and Campaign Notifications</th>
                </tr>
              </thead>
              {makeBrandItems(brandSubscriptions)}
            </table>
            <table className={styles.subscriptionsTable}>
              <thead>
                <tr>
                  <th>Stock Notifications</th>
                </tr>
              </thead>
              {makeStockItems(stockSubscriptions)}
            </table>
          </div>
        }
      </div>
    );
  }

  render() {
    const { subscriptionsInfo } = this.props;
    const { isLoaded } = subscriptionsInfo;
    const { onUnsubscribeFromAll, onSaveChanges, makeSubscriptionsTable, makeAccountHeader } = this;

    return (
      <SiteAwareMetadata>
        <div className={styles.subscriptions}>
          {makeAccountHeader()}
          {makeSubscriptionsTable()}
          <div className={styles.buttons}>
            <button
              type="button"
              className={styles.saveButton}
              disabled={!isLoaded}
              onClick={onSaveChanges}>
              Save Changes
            </button>
            <button
              type="button"
              className={styles.unsubscribeButton}
              disabled={!isLoaded}
              onClick={onUnsubscribeFromAll}>
              Unsubscribe From All Mailings
            </button>
            <p>Click the "Unsubscribe From All Mailings" button to stop receiving all Zappos marketing communications
              (except transactional and account related emails).</p>
          </div>
        </div>
      </SiteAwareMetadata>
    );
  }
}

export function mapStateToProps(state) {
  return {
    account: state.account,
    subscriptionsInfo: state.subscriptionsInfo
  };
}

export default connect(mapStateToProps, {
  fetchSubscriptionsInfo,
  updateSubscriptions,
  toggleSubscriptionList,
  toggleSubscriptionStock,
  toggleSubscriptionBrand,
  fetchAccountInfo
})(Subscriptions);
